import React, { useState, useEffect } from 'react';
import { getStaffAttendance, getStaffAttendanceReport } from '../storage';
import { exportCSV } from '../db';
import { Badge, Btn, Card, Modal, NoData, Stat, toast } from '../ui.jsx';

// ── Teacher My Attendance ─────────────────────────────────────────
function TeacherMyAttendance({ teacher, activeSessionId }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calOpen, setCalOpen] = useState(false);
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const tid = teacher.tid || teacher.id;

  const today = new Date().toISOString().split('T')[0];
  const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  useEffect(() => {
    if (!activeSessionId || !tid) { setLoading(false); return; }
    setLoading(true);
    getStaffAttendanceReport(activeSessionId, tid)
      .then(data => { setRecords(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => { setRecords([]); setLoading(false); });
  }, [activeSessionId, tid]);

  const attMap = {};
  records.forEach(r => { if (r.date) attMap[r.date] = r; });

  const statusColor = s => s === 'Present' ? '#059669' : s === 'Absent' ? '#ef4444' : s === 'Half Day' ? '#d97706' : '#6366f1';
  const statusBg = s => s === 'Present' ? '#d1fae5' : s === 'Absent' ? '#fee2e2' : s === 'Half Day' ? '#fef3c7' : '#ede9fe';
  const statusIcon = s => s === 'Present' ? 'check_circle' : s === 'Absent' ? 'cancel' : s === 'Half Day' ? 'schedule' : 'event_available';

  const present = records.filter(r => r.status === 'Present').length;
  const absent = records.filter(r => r.status === 'Absent').length;
  const half = records.filter(r => r.status === 'Half Day').length;
  const leave = records.filter(r => r.status === 'Leave').length;
  const total = records.length;
  const pct = total > 0 ? Math.round((present + half * 0.5) / total * 100) : 0;
  const pctCol = pct >= 90 ? '#059669' : pct >= 75 ? '#d97706' : '#ef4444';

  const monthlyData = {};
  records.forEach(r => {
    if (!r.date) return;
    const mo = new Date(r.date).getMonth();
    if (!monthlyData[mo]) monthlyData[mo] = { present: 0, absent: 0, half: 0, leave: 0 };
    if (r.status === 'Present') monthlyData[mo].present++;
    else if (r.status === 'Absent') monthlyData[mo].absent++;
    else if (r.status === 'Half Day') monthlyData[mo].half++;
    else if (r.status === 'Leave') monthlyData[mo].leave++;
  });
  const chartMonths = Object.keys(monthlyData).map(Number).sort((a, b) => a - b).slice(-6);
  const todayRec = attMap[today];
  const sorted = [...records].sort((a, b) => b.date > a.date ? 1 : -1);

  const prevMonth = () => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); };
  const nextMonth = () => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); };
  const firstDay = new Date(calYear, calMonth, 1).getDay();
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const calCells = [];
  for (let i = 0; i < firstDay; i++) calCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calCells.push(d);
  const dateStr = d => `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const recOf = d => d ? attMap[dateStr(d)] : null;
  const cellStyle = d => {
    const r = recOf(d); const isToday = d && today === dateStr(d);
    if (!d) return { background: 'transparent', border: 'none' };
    if (r?.status === 'Present') return { background: '#dcfce7', border: '1.5px solid #86efac', color: '#059669' };
    if (r?.status === 'Absent') return { background: '#fee2e2', border: '1.5px solid #fca5a5', color: '#ef4444' };
    if (r?.status === 'Half Day') return { background: '#fef3c7', border: '1.5px solid #fcd34d', color: '#d97706' };
    if (r?.status === 'Leave') return { background: '#ede9fe', border: '1.5px solid #c4b5fd', color: '#6366f1' };
    if (isToday) return { background: '#eef4ff', border: '1.5px solid #93c5fd', color: '#1960a3' };
    return { background: '#f8fafc', border: '1px solid #f1f5f9', color: '#94a3b8' };
  };
  const mData = monthlyData[calMonth] || { present: 0, absent: 0, half: 0, leave: 0 };
  const mTotal = mData.present + mData.absent + mData.half + mData.leave;

  return (
    <div style={{ fontFamily: 'Inter,sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 32 }}>
        <div>
          <p style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 4 }}>My Records</p>
          <h1 style={{ fontSize: 32, fontWeight: 900, color: '#1e293b', fontFamily: 'Manrope,sans-serif', margin: 0 }}>My Attendance</h1>
        </div>
        <button onClick={() => setCalOpen(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 12, border: '1.5px solid #c7d9f5', background: '#eef4ff', color: '#1960a3', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18, fontVariationSettings: "'FILL' 1" }}>calendar_month</span>
          Calendar
        </button>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Present', val: present, c: '#059669', bg: '#d1fae5', icon: 'check_circle' },
          { label: 'Absent', val: absent, c: '#ef4444', bg: '#fee2e2', icon: 'cancel' },
          { label: 'Half Day', val: half, c: '#d97706', bg: '#fef3c7', icon: 'schedule' },
          { label: 'Leave', val: leave, c: '#6366f1', bg: '#ede9fe', icon: 'event_available' },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', borderRadius: 16, padding: '18px 16px', boxShadow: '0 2px 12px rgba(0,31,77,0.07)', border: '1px solid #f1f5f9', textAlign: 'center' }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 18, color: s.c, fontVariationSettings: "'FILL' 1" }}>{s.icon}</span>
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: s.c, fontFamily: 'Manrope,sans-serif' }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Attendance rate bar */}
      {total > 0 && (
        <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 2px 12px rgba(0,31,77,0.07)', border: '1px solid #f1f5f9', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Overall Attendance Rate</span>
            <span style={{ fontSize: 20, fontWeight: 900, color: pctCol, fontFamily: 'Manrope,sans-serif' }}>{pct}%</span>
          </div>
          <div style={{ height: 10, borderRadius: 99, background: '#f1f5f9', overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 99, width: `${pct}%`, background: `linear-gradient(90deg,${pctCol},${pctCol}cc)`, transition: 'width 800ms ease' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
            <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>0%</span>
            <span style={{ fontSize: 10, color: pctCol, fontWeight: 700 }}>{pct >= 90 ? 'Excellent' : pct >= 75 ? 'Satisfactory' : 'Below Required (75%)'}</span>
            <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>100%</span>
          </div>
        </div>
      )}

      {/* Monthly bar chart */}
      {chartMonths.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 2px 12px rgba(0,31,77,0.07)', border: '1px solid #f1f5f9', marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 16 }}>Monthly Attendance</div>
          <div style={{ overflowX: 'auto' }}>
            <svg width={Math.max(chartMonths.length * 70, 300)} height={130} viewBox={`0 0 ${Math.max(chartMonths.length * 70, 300)} 130`} style={{ display: 'block' }}>
              {chartMonths.map((mo, i) => {
                const d = monthlyData[mo] || { present: 0, absent: 0, half: 0, leave: 0 };
                const tot = d.present + d.absent + d.half + d.leave || 1;
                const rate = (d.present + d.half * 0.5) / tot;
                const barH = Math.round(rate * 72);
                const bc = rate >= 0.9 ? '#059669' : rate >= 0.75 ? '#d97706' : '#ef4444';
                const x = i * 70 + 10;
                return (
                  <g key={mo} onClick={() => { setCalMonth(mo); setCalYear(new Date().getFullYear()); }} style={{ cursor: 'pointer' }}>
                    <rect x={x} y={90 - barH} width={50} height={barH} rx={6} fill={bc} opacity={calMonth === mo ? 1 : 0.65} />
                    <text x={x + 25} y={108} textAnchor="middle" fontSize="9" fontWeight="700" fill="#64748b">{MONTHS_SHORT[mo]}</text>
                    <text x={x + 25} y={Math.max(90 - barH - 4, 12)} textAnchor="middle" fontSize="9" fontWeight="800" fill={bc}>{Math.round(rate * 100)}%</text>
                  </g>
                );
              })}
              <line x1="0" y1="90" x2={Math.max(chartMonths.length * 70, 300)} y2="90" stroke="#f1f5f9" strokeWidth="1" />
            </svg>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Tap a bar to jump to that month's calendar</div>
        </div>
      )}

      {/* Today card */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 2px 12px rgba(0,31,77,0.07)', border: '1px solid #f1f5f9', marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#1960a3', fontVariationSettings: "'FILL' 1" }}>today</span>
          Today — {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8' }}>Loading…</div>
        ) : todayRec ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 120, background: statusBg(todayRec.status), borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: statusColor(todayRec.status), marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: statusColor(todayRec.status), fontFamily: 'Manrope,sans-serif' }}>{todayRec.status}</div>
            </div>
            <div style={{ flex: 1, minWidth: 120, background: '#f8fafc', borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Type</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>{todayRec.type || 'Full Day'}</div>
            </div>
            {todayRec.inTime && <div style={{ flex: 1, minWidth: 120, background: '#f0fdf4', borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#059669', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>In Time</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>{todayRec.inTime}</div>
            </div>}
            {todayRec.outTime && <div style={{ flex: 1, minWidth: 120, background: '#fff7ed', borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#d97706', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Out Time</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>{todayRec.outTime}</div>
            </div>}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 36, display: 'block', marginBottom: 6, opacity: 0.35 }}>event_busy</span>
            No attendance marked for today yet.
          </div>
        )}
      </div>

      {/* History table */}
      <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 12px rgba(0,31,77,0.07)', overflow: 'hidden', border: '1px solid #f1f5f9' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Attendance History</div>
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
        ) : sorted.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 32, color: '#e2e8f0', display: 'block', margin: '0 auto 8px' }}>assignment</span>
            No attendance records yet
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Date', 'Day', 'Status', 'Type', 'In', 'Out'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', background: '#f8fafc' }}>{h}</th>
              ))}</tr></thead>
              <tbody>{sorted.slice(0, 90).map((r, i) => {
                const dt = new Date(r.date);
                const sc = statusColor(r.status);
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 16px', fontSize: 13, color: '#1e293b', fontWeight: 600 }}>
                      {dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {r.date === today && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, background: '#dbeafe', color: '#1d4ed8', padding: '2px 7px', borderRadius: 20 }}>Today</span>}
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: '#64748b' }}>{dt.toLocaleDateString('en-IN', { weekday: 'short' })}</td>
                    <td style={{ padding: '10px 16px' }}><span style={{ padding: '3px 10px', borderRadius: 20, background: statusBg(r.status), color: sc, fontSize: 11, fontWeight: 700 }}>{r.status}</span></td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: '#64748b' }}>{r.type || 'Full Day'}</td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: '#64748b' }}>{r.inTime || '—'}</td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: '#64748b' }}>{r.outTime || '—'}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}
      </div>

      {/* Calendar Modal */}
      {calOpen && (
        <div onClick={() => { setCalOpen(false); setSelectedDate(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 20, padding: 24, width: '100%', maxWidth: 400, boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <button onClick={prevMonth} style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chevron_left</span>
              </button>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#1e293b', fontFamily: 'Manrope,sans-serif' }}>{MONTHS_FULL[calMonth]} {calYear}</div>
                {mTotal > 0 && <div style={{ fontSize: 11, color: ((mData.present + mData.half * 0.5) / mTotal * 100) >= 75 ? '#059669' : '#ef4444', fontWeight: 700, marginTop: 2 }}>
                  {mData.present}P · {mData.absent}A · {mData.half}H · {mData.leave}L
                </div>}
              </div>
              <button onClick={nextMonth} style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>chevron_right</span>
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 3, marginBottom: 3 }}>
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                <div key={i} style={{ textAlign: 'center', fontSize: 10, fontWeight: 800, color: '#94a3b8', padding: '3px 0' }}>{d}</div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 3 }}>
              {calCells.map((d, i) => {
                const cs = cellStyle(d); const r = recOf(d);
                const ds = d ? dateStr(d) : null; const isSel = ds && selectedDate === ds;
                return (
                  <div key={i} onClick={() => d && setSelectedDate(ds === selectedDate ? null : ds)}
                    style={{
                      aspectRatio: '1', borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      cursor: d ? 'pointer' : 'default', minHeight: 38,
                      background: isSel ? '#1960a3' : cs.background, border: isSel ? '2px solid #1960a3' : cs.border,
                      color: isSel ? '#fff' : cs.color, transform: isSel ? 'scale(1.1)' : 'none', transition: 'all 150ms'
                    }}>
                    {d && <>
                      <span style={{ fontSize: 13, fontWeight: 700, lineHeight: 1 }}>{d}</span>
                      {r && <span style={{ fontSize: 7, fontWeight: 800, lineHeight: 1, marginTop: 1 }}>{r.status === 'Present' ? 'P' : r.status === 'Absent' ? 'A' : r.status === 'Half Day' ? 'H' : 'L'}</span>}
                    </>}
                  </div>
                );
              })}
            </div>
            {selectedDate && (() => {
              const r = attMap[selectedDate]; const dt = new Date(selectedDate);
              const sc = r ? statusColor(r.status) : '#94a3b8';
              const bg = r ? statusBg(r.status) : '#f8fafc';
              const icon = r ? statusIcon(r.status) : 'event_busy';
              return (
                <div style={{ marginTop: 14, padding: '14px 16px', borderRadius: 12, background: bg, border: `1.5px solid ${sc}33` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: (r?.inTime || r?.outTime) ? 10 : 0 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 26, color: sc, fontVariationSettings: "'FILL' 1", flexShrink: 0 }}>{icon}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>{dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{dt.toLocaleDateString('en-IN', { weekday: 'long' })}</div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: sc, marginTop: 4 }}>{r ? r.status : 'No Record'}{r?.type && r.type !== 'Full Day' ? ` · ${r.type}` : ''}</div>
                    </div>
                  </div>
                  {(r?.inTime || r?.outTime) && (
                    <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                      {r.inTime && <div style={{ flex: 1, background: 'rgba(255,255,255,0.7)', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.06em' }}>In</div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: '#1e293b' }}>{r.inTime}</div>
                      </div>}
                      {r.outTime && <div style={{ flex: 1, background: 'rgba(255,255,255,0.7)', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Out</div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: '#1e293b' }}>{r.outTime}</div>
                      </div>}
                    </div>
                  )}
                </div>
              );
            })()}
            <div style={{ display: 'flex', gap: 12, marginTop: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
              {[['#059669', '#dcfce7', 'Present'], ['#ef4444', '#fee2e2', 'Absent'], ['#d97706', '#fef3c7', 'Half Day'], ['#6366f1', '#ede9fe', 'Leave']].map(([c, bg, lbl]) => (
                <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: bg, border: `1.5px solid ${c}55` }} />
                  <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{lbl}</span>
                </div>
              ))}
            </div>
            <button onClick={() => { setCalOpen(false); setSelectedDate(null); }}
              style={{ width: '100%', marginTop: 16, padding: '10px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: 'transparent', color: '#64748b', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


export default TeacherMyAttendance;
